import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { constructQueryParamName } from '../../util/search';
import NamedLink from '../NamedLink/NamedLink';

import { resolveGroups, categorySearchString, FALLBACK_CATEGORIES } from './categoryGroups';
import css from './CategoryShortcuts.module.css';

const ICON_PROPS = { size: 18, strokeWidth: 1.5 };

/**
 * The three space types as flat shortcuts under a hero search: one click, one type, into the
 * results. This is now the ONLY place a visitor picks a space type before searching — a header
 * filter and a pill bar on the city pages both existed for a day and were removed as clutter.
 *
 * Built on `resolveGroups` from `categoryGroups` rather than on its own list. Two taxonomies that
 * mean the same thing drift apart — that is finding B1 of the CategoryBar plan, where the homepage
 * and the city pages had grown two different sets of categories and nobody noticed until one of
 * them started showing "Coliving" next to "Meeting".
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.boundsStr - city bounds; omit for a Switzerland-wide link. With it, the
 *   shortcut keeps the visitor inside the city they are already looking at, which is the whole
 *   reason the city pages get their own copy.
 * @returns {JSX.Element|null}
 */
const CategoryShortcuts = props => {
  const { className, boundsStr } = props;

  const intl = useIntl();
  const config = useConfiguration();

  const categoryConfiguration = config?.categoryConfiguration;
  const hostedCategories = categoryConfiguration?.categories ?? [];
  const categoryKey = categoryConfiguration?.key || 'categoryLevel';

  const categories = hostedCategories.length > 0 ? hostedCategories : FALLBACK_CATEGORIES;
  const groups = resolveGroups(categories);

  if (groups.length === 0) {
    return null;
  }

  const paramName = constructQueryParamName(`${categoryKey}1`, 'public');

  const searchFor = group => categorySearchString(paramName, group.ids, boundsStr);

  return (
    <nav
      className={classNames(css.root, className)}
      aria-label={intl.formatMessage({ id: 'CategoryShortcuts.groupLabel' })}
    >
      {groups.map(({ key, Icon, labelId, ids }) => (
        <NamedLink
          key={key}
          name="SearchPage"
          to={{ search: searchFor({ ids }) }}
          className={css.shortcut}
          /* Same click id as the header filter: both are "a visitor chose a space type", and
             splitting them into two events would mean neither number answers the question the
             plan's measurement section actually asks. */
          dataTrackCta="category_pill"
        >
          <span className={css.icon} aria-hidden="true">
            <Icon {...ICON_PROPS} />
          </span>
          <span className={css.label}>{intl.formatMessage({ id: labelId })}</span>
        </NamedLink>
      ))}
    </nav>
  );
};

export default CategoryShortcuts;
